/**
 * Model Factory
 *
 * Generates model definitions for an event module config.
 * Each base model template is a function that accepts a config and returns
 * the model object with the correct serviceKey and key prefix.
 *
 * Model types:
 * 1. Simple models - key is a plain string (e.g., "emailClient"), only serviceKey varies
 * 2. Settings models - key is prefixed (e.g., "HrdcEinvoicingSettingsPrice"), both key and serviceKey vary
 * 3. Application model - has additional config-driven logic (pricing, crmViewKey, attendance list)
 */

import { createApplicationModel } from "./application";
import { createRefundApToCimbModel } from "./refundApToCimb";

// Simple model templates - only serviceKey differs
const simpleModelTemplates = {
  emailClient: {
    key: "emailClient",
    name: { singular: "Send HRDC Email To Client", plural: "Send HRDC Email To Client" },
    parents: [],
    add: { name: "Send HRDC Email To Client" },
  },
  revertForm: {
    key: "revertForm",
    name: { singular: "Grant Approval & Revert Form", plural: "Grant Approval & Revert Form" },
    parents: [],
    add: { name: "Grant Approval & Revert Form" },
    view: { name: "View Application" },
  },
  manualInvoice: {
    key: "manualInvoice",
    name: { singular: "Manual Invoice to HRDC", plural: "Manual Invoice to HRDC" },
    parents: [],
    add: { name: "Manual Invoice to HRDC" },
    view: { name: "View Application" },
  },
  claimSubmit: {
    key: "claimSubmit",
    name: { singular: "Claim Submit to HRDC", plural: "Claim Submit to HRDC" },
    parents: [],
    add: { name: "Claim Submit to HRDC" },
    view: { name: "View Application" },
  },
  claimApproved: {
    key: "claimApproved",
    name: { singular: "Claim Approved From HRDC", plural: "Claim Approved From HRDC" },
    parents: [],
    add: { name: "Claim Approved From HRDC" },
  },
  paymentReceived: {
    key: "paymentReceived",
    name: { singular: "Payment Received From HRDC", plural: "Payment Received From HRDC" },
    parents: [],
    add: { name: "Payment Received From HRDC" },
  },
  creditNoteGenerate: {
    key: "creditNoteGenerate",
    name: { singular: "Credit Note Generate", plural: "Credit Note Generate" },
    parents: [],
    add: { name: "Credit Note Generate" },
  },
  creditNoteRequested: {
    key: "creditNoteRequested",
    name: { singular: "Credit Note Requested", plural: "Credit Note Requested" },
    parents: [],
    add: { name: "Credit Note Requested" },
  },
  creditNoteDocumented: {
    key: "creditNoteDocumented",
    name: { singular: "Credit Note Documented", plural: "Credit Note Documented" },
    parents: [],
    add: { name: "Credit Note Documented" },
  },
  refundEmailSubmission: {
    key: "refundEmailSubmission",
    name: { singular: "Refund Email Submission", plural: "Refund Email Submission" },
    parents: [],
    add: { name: "Refund Email Submission" },
  },
  refundEmailApproval1: {
    key: "refundEmailApproval1",
    name: { singular: "Refund Email Approval 1", plural: "Refund Email Approval 1" },
    parents: [],
    add: { name: "Refund Email Approval 1" },
  },
  refundEmailApproval2: {
    key: "refundEmailApproval2",
    name: { singular: "Refund Email Approval 2", plural: "Refund Email Approval 2" },
    parents: [],
    add: { name: "Refund Email Approval 2" },
  },
  refundEmailArToAp: {
    key: "refundEmailArToAp",
    name: { singular: "Refund Email AR to AP", plural: "Refund Email AR to AP" },
    parents: [],
    add: { name: "Refund Email AR to AP" },
  },
  financeRefundToClient: {
    key: "financeRefundToClient",
    name: { singular: "Finance Refund To Client", plural: "Finance Refund To Client" },
    parents: [],
    add: { name: "Finance Refund To Client" },
  },
  refundFailed: {
    key: "refundFailed",
    name: { singular: "Refund Failed", plural: "Refund Failed" },
    parents: [],
    add: { name: "Refund Failed" },
  },
  emailPaymentAdviceToClient: {
    key: "emailPaymentAdviceToClient",
    name: { singular: "Email Payment Advice To Client", plural: "Email Payment Advice To Client" },
    parents: [],
    add: { name: "Email Payment Advice To Client" },
  },
  enrollmentDetail: {
    key: "enrollmentDetail",
    name: { singular: "Enrollment Detail", plural: "Enrollment Details" },
    parents: [],
    add: { name: "Enrollment Detail" },
  },
  grantNoSubmit: {
    key: "grantNoSubmit",
    name: { singular: "Grant No Submit", plural: "Grant No Submit" },
    parents: [],
    add: { name: "Grant No Submit" },
  },
  generalInfo: {
    key: "generalInfo",
    name: { singular: "General Info", plural: "General Info" },
    parents: [],
    add: { name: "General Info" },
  },
  submissionDate: {
    key: "submissionDate",
    name: { singular: "Submission Date", plural: "Submission Date" },
    parents: [],
    add: { name: "Submission Date" },
  },
  reportAnalysis: {
    key: "reportAnalysis",
    name: { singular: "Report Analysis", plural: "Report Analysis" },
    parents: [],
    add: { name: "Report Analysis" },
  },
};

// Settings model templates - key is prefixed with module PascalCase name
const settingsModelTemplates = {
  SettingsPrice: {
    nameSuffix: "SettingsPrice",
    name: { singular: "Price", plural: "Price" },
    parents: [],
    add: { name: "Price" },
    view: { name: "Price" },
  },
  SettingsErrorLog: {
    nameSuffix: "SettingsErrorLog",
    name: { singular: "Error Log", plural: "Error Log" },
    parents: [],
    add: { name: "Error Log" },
    view: { name: "Error Log" },
  },
  SettingsEvents: {
    nameSuffix: "SettingsEvents",
    name: { singular: "Event", plural: "Event" },
    parents: [],
    add: { name: "Event" },
    view: { name: "Event" },
  },
  SettingsXero: {
    nameSuffix: "SettingsXero",
    name: { singular: "Xero", plural: "Xero" },
    parents: [],
    add: { name: "Xero" },
    view: { name: "Xero" },
  },
  SettingsAttachment: {
    nameSuffix: "SettingsAttachment",
    name: { singular: "Attachment", plural: "Attachments" },
    parents: [],
    add: { name: "Attachment" },
    view: { name: "Attachment" },
  },
};

/**
 * Get the PascalCase prefix from a config's modelKeyPrefix.
 * e.g., "hrdcEinvoicing" -> "HrdcEinvoicing"
 */
function getPascalPrefix(config) {
  return config.modelKeyPrefix.charAt(0).toUpperCase() + config.modelKeyPrefix.slice(1);
}

/**
 * Generate a simple model for a given config.
 */
function createSimpleModel(template, config) {
  return {
    ...template,
    serviceKey: config.serviceKey,
  };
}

/**
 * Generate a settings model for a given config.
 */
function createSettingsModel(template, config) {
  const prefix = getPascalPrefix(config);
  const model = {
    key: `${prefix}${template.nameSuffix}`,
    serviceKey: config.serviceKey,
    name: { ...template.name },
    parents: [...template.parents],
    add: { ...template.add },
  };
  if (template.view) model.view = { ...template.view };

  // Attachment model has parent_route
  if (template.nameSuffix === "SettingsAttachment") {
    model.parent_route = `${config.routePrefix}Settings`;
  }

  return model;
}

/**
 * Generate ALL models for a single event module config.
 * Returns an object keyed by the model's import name (e.g., "hrdcEinvoicingApplication").
 *
 * @param {object} config - Event module config from eventRegistry
 * @returns {object} Map of modelImportKey -> model definition
 */
export function generateModelsForConfig(config) {
  const prefix = config.modelKeyPrefix; // e.g., "hrdcEinvoicing"
  const Prefix = getPascalPrefix(config); // e.g., "HrdcEinvoicing"
  const models = {};

  // Application model (complex - has pricing, crmViewKey, attendance list)
  models[`${prefix}Application`] = createApplicationModel(config);

  // Simple models
  for (const [name, template] of Object.entries(simpleModelTemplates)) {
    const importKey = `${prefix}${name.charAt(0).toUpperCase()}${name.slice(1)}`;
    models[importKey] = createSimpleModel(template, config);
  }

  // Settings models
  for (const [, template] of Object.entries(settingsModelTemplates)) {
    const importKey = `${Prefix}${template.nameSuffix}`;
    models[importKey] = createSettingsModel(template, config);
  }

  // RefundApToCimb (has browse config with tabulator headers - parameterized separately)
  models[`${prefix}RefundEmailApToCimb`] = createRefundApToCimbModel(config);

  return models;
}
